import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE } from './apiConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartBroken, faArrowLeft, faTrashAlt, faChevronRight, faCircleNotch } from '@fortawesome/free-solid-svg-icons';

const Favorites = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const goldColor = '#c5a059';
  const darkBlue = '#0f172a';

  const user = JSON.parse(localStorage.getItem('liber_user'));
  const token = localStorage.getItem('token'); 

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!user || !token) {
      navigate('/user-login');
      return;
    }
    fetchFavorites();
  }, [i18n.language]); // Վերաբեռնել, եթե լեզուն փոխվի

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const headers = { 
       
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      };

      // 1. Ստանում ենք սիրվածների ID-ների ցանկը
      const favRes = await fetch(`${API_BASE}/api/favorites/${user.id}`, { headers });
      
      if (favRes.status === 401) {
        localStorage.removeItem('token');
        navigate('/user-login');
        return;
      }

      const favData = await favRes.json();
      // Ապահովագրում ենք, որ լինի String ID-ների զանգված
      const favIds = Array.isArray(favData) ? favData.map(id => String(id)) : [];

      if (favIds.length === 0) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      // 2. Ստանում ենք բոլոր ենթածառայությունները՝ սիրվածները ֆիլտրելու համար
      const subRes = await fetch(`${API_BASE}/api/subservices`, { headers });
      const allSubservices = await subRes.json();

      if (Array.isArray(allSubservices)) {
        // Համեմատում ենք String դարձրած ID-ները
        const myFavs = allSubservices.filter(s => favIds.includes(String(s.id)));
        setFavorites(myFavs);
      }
      
    } catch (err) {
      console.error("Error fetching favorites:", err);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (e, subId) => {
    e.stopPropagation(); // Որպեսզի քարտի սեղմվելը չաշխատի
    try {
      const res = await fetch(`${API_BASE}/api/favorites/toggle`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
         
        },
        body: JSON.stringify({ user_id: user.id, subservice_id: subId })
      });
      
      const result = await res.json();
      if (result.success || res.ok) {
        setFavorites(prev => prev.filter(f => String(f.id) !== String(subId)));
      }
    } catch (err) {
      console.error("Error removing favorite:", err);
    }
  };

  const getFullImagePath = (img) => {
    if (!img) return "https://via.placeholder.com/300x200?text=No+Image";
    if (img.startsWith('http')) return img;
    const path = img.startsWith('/') ? img : `/uploads/${img}`;
    return `${API_BASE}${path}`.replace(/([^:]\/)\/+/g, "$1");
  };

  if (loading) {
    return (
      <div style={loaderStyle}>
        <FontAwesomeIcon icon={faCircleNotch} spin size="3x" color={goldColor} />
        <p style={{marginTop: '20px'}}>{t('fav_loading') || 'Loading favorites...'}</p>
      </div>
    );
  }

  return (
    <div style={pageContainerStyle}>
      <div style={contentWrapper}>
        <button onClick={() => navigate(-1)} style={backBtnStyle}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t('fav_back') || 'Back'}
        </button>

        <div style={headerStyle}>
          <h2 style={titleStyle}>{t('fav_title') || 'My Favorites'}</h2>
          <p style={subtitleStyle}>{t('fav_subtitle') || 'Services you have saved for later'}</p>
        </div>

        {favorites.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={iconCircle}>
              <FontAwesomeIcon icon={faHeartBroken} style={{ fontSize: '40px', color: '#94a3b8' }} />
            </div>
            <h3 style={{ color: darkBlue, marginBottom: '10px' }}>{t('fav_empty_title') || 'No favorites yet'}</h3>
            <p style={{ color: '#64748b', marginBottom: '25px' }}>{t('fav_empty_text') || 'Explore our services and add them to your list.'}</p>
            <Link to="/" style={exploreBtnStyle}>{t('fav_explore_btn') || 'Explore Services'}</Link>
          </div>
        ) : (
          <div style={gridStyle}>
            {favorites.map(sub => (
              <div 
                key={sub.id} 
                style={cardStyle} 
                onClick={() => navigate(`/subservice/${sub.id}`)}
              >
                <div style={imageWrapper}>
                <img 
    src={getFullImagePath(sub.image)} 
    alt={sub.name} 
    style={imgStyle} 
    loading="lazy"      // 1. Բեռնում է միայն այն ժամանակ, երբ էկրանին է
    decoding="async"    // 2. Թույլ չի տալիս, որ նկարը մշակելիս էջը "կախի"
    onError={(e) => { 
        e.target.onerror = null; // Կանխում ենք անվերջ loop-ը
        e.target.src = "https://via.placeholder.com/300x200?text=No+Image"; 
    }} 
/>
                  <button 
                    onClick={(e) => removeFavorite(e, sub.id)} 
                    style={removeBtnStyle}
                    title="Remove from favorites"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
                <div style={cardContent}>
                  <h3 style={cardTitleStyle}>
                    {sub[`name_${i18n.language}`] || sub.name}
                  </h3>
                  <div style={cardFooter}>
                    <span style={priceStyle}>
                      {sub.price ? `${Number(sub.price).toLocaleString()} ֏` : (t('fav_price_contract') || 'Contract')}
                    </span>
                    <div style={arrowCircle}>
                       <FontAwesomeIcon icon={faChevronRight} style={{fontSize: '10px', color: '#fff'}} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Styles ---
const pageContainerStyle = { padding: '140px 20px 80px', background: '#f8fafc', minHeight: '100vh' };
const contentWrapper = { maxWidth: '1100px', margin: '0 auto' };
const headerStyle = { marginBottom: '40px', textAlign: 'left' };
const titleStyle = { fontSize: '32px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' };
const subtitleStyle = { color: '#64748b', fontSize: '16px' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' };
const cardStyle = { background: '#fff', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.03)', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.3s ease', border: '1px solid #f1f5f9' };
const imageWrapper = { height: '200px', position: 'relative', overflow: 'hidden' };
const imgStyle = { width: '100%', height: '100%', objectFit: 'cover' };
const removeBtnStyle = { position: 'absolute', top: '15px', right: '15px', background: 'rgba(255, 255, 255, 0.9)', border: 'none', borderRadius: '12px', width: '38px', height: '38px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 };
const cardContent = { padding: '20px' };
const cardTitleStyle = { fontSize: '17px', fontWeight: '700', color: '#1e293b', marginBottom: '15px', height: '40px', overflow: 'hidden' };
const cardFooter = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const priceStyle = { color: '#c5a059', fontWeight: '800', fontSize: '18px' };
const arrowCircle = { width: '28px', height: '28px', borderRadius: '50%', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const backBtnStyle = { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '15px', fontWeight: '600', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '8px' };
const emptyStateStyle = { textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center' };
const iconCircle = { width: '90px', height: '90px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' };
const exploreBtnStyle = { padding: '14px 28px', background: '#c5a059', color: '#fff', textDecoration: 'none', borderRadius: '16px', fontWeight: '700' };
const loaderStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8fafc' };

export default Favorites;